import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Sentry
import * as Sentry from '@sentry/react';

// Components
import ModalContainer from '../../containers/ModalContainer';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import { Typography } from '../../../display/Typography';

// Styled Components
import {
  ModalContent,
  FormContainer,
  MaterialName,
  HelpText,
  StickyFooter,
  CancelButton,
  SubmitButton,
} from './styles';

// Types
import { ModalProps } from './types';
import { Options } from '../../../inputs/DropDown/MultiSelectDropdown/types';

// GraphQL
import { client } from '../../../../gql/client';
import { SET_MATERIAL_TEAMS } from '../../../../gql/mutations/materials';

// Utils
import { showSuccessToast, showErrorToast } from '../../../../utils/toasts';

/**
 * Asignar equipos a un material (2026-07-31).
 *
 * Deliberadamente tiene UN solo control: la mutation del backend
 * (`setMaterialTeams`) solo escribe `teamIDs`, porque un material puede ser
 * GLOBAL y compartido entre tenants — editar aquí su nombre afectaría a
 * terceros. Si algún día se quiere edición completa, hay que resolver eso
 * primero en el backend.
 *
 * Sin equipos seleccionados el material queda disponible para todos: es el
 * estado por defecto de todo el catálogo y por eso la feature no rompe nada
 * el día que se despliega.
 */
export const EditMaterialTeamsModal = ({
  onClickClose,
  data,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['forms', 'common']);
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(
    data?.material?.teamIDs ?? []
  );

  const teamOptions: Options = useMemo(
    () =>
      (data?.teams ?? []).map((team) => ({
        value: team.id,
        label: team.name,
      })),
    [data?.teams]
  );

  const onSubmit = async () => {
    if (!data?.material?.id) return;
    setIsSubmitting(true);
    try {
      await client.mutate({
        mutation: SET_MATERIAL_TEAMS,
        variables: {
          materialId: data.material.id,
          teamIds: selectedTeamIds,
        },
      });
      showSuccessToast(t('management.materialTeams.success'));
      data.onSuccess?.();
      onClickClose();
    } catch (err) {
      Sentry.captureException(err);
      showErrorToast(t('management.materialTeams.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContainer
      width={50}
      onClickClose={onClickClose}
      fill={theme.colors.black}
      title={t('management.materialTeams.editTitle')}
      {...props}
    >
      <ModalContent>
        <FormContainer>
          <MaterialName size="M" variant="semiBold">
            {`${data?.material?.materialId ?? ''} · ${
              data?.material?.name ?? ''
            }`}
          </MaterialName>

          <Typography size="S" variant="semiBold">
            {t('management.materialTeams.title')}
          </Typography>

          <MultiSelectDropdown
            inputSize="XL"
            typeDropDown="Default"
            placeholder={t('management.materialTeams.placeholder')}
            options={teamOptions}
            selectedValues={selectedTeamIds}
            onChangeSelected={setSelectedTeamIds}
          />

          {/*
            Solo con la selección vacía: el texto describe ESE estado, y
            dejarlo puesto con equipos ya elegidos se lee como si el material
            siguiera siendo de todos (cazado en la validación en navegador).
          */}
          {selectedTeamIds.length === 0 && (
            <HelpText size="XS" variant="regular">
              {t('management.materialTeams.help')}
            </HelpText>
          )}
        </FormContainer>

        <StickyFooter>
          <CancelButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isSubmitting}
          />
          {/*
            El estado "guardando" se comunica con `disabled`, no cambiando el
            texto: `common:buttons.saving` NO existe en los locales (el
            EditTeamModal la usa con un fallback castellano hardcodeado — bug
            i18n suyo que no replicamos aquí).
          */}
          <SubmitButton
            type="button"
            text={t('common:buttons.save')}
            onClick={onSubmit}
            disabled={isSubmitting}
          />
        </StickyFooter>
      </ModalContent>
    </ModalContainer>
  );
};
